import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Components
import Navbar from './components/Navbar';
import HomePage from './components/HomePage';
import PastEventsPage from './components/PastEventsPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './components/Dashboard';
import ScrollToTop from './components/ScrollToTop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults for better performance
axios.defaults.timeout = 30000; // 30 second timeout
axios.defaults.headers.common['Cache-Control'] = 'no-cache';

// Configure axios interceptor to add auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle errors globally with conservative logout behavior
let isLoggingOut = false;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Retry on timeout or network errors (max 2 retries)
    if (!config._retryCount) config._retryCount = 0;
    
    const isRetryable = (
      (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) ||
      (!error.response && error.message?.includes('Network Error'))
    );
    
    if (isRetryable && config._retryCount < 2) {
      config._retryCount += 1;
      await new Promise(r => setTimeout(r, config._retryCount * 1000));
      return axios(config);
    }
    
    // Only auto-logout on EXPLICIT JWT expiration from the backend
    if (error.response?.status === 401 && !isLoggingOut) {
      const errorMessage = (error.response?.data?.detail || '').toLowerCase();
      const isJwtExpired = errorMessage.includes('expired') || 
                           errorMessage.includes('scadut');
      
      if (isJwtExpired) {
        const currentPath = window.location.pathname;
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/', '/eventi-passati'];
        
        if (!publicPaths.includes(currentPath)) {
          isLoggingOut = true;
          // Show notification BEFORE logout
          try {
            const { toast } = await import('sonner');
            toast.error('Sessione scaduta. Effettua nuovamente il login.');
          } catch {}
          // Delay logout slightly so user sees the notification
          setTimeout(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
            setTimeout(() => { isLoggingOut = false; }, 2000);
          }, 1500);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      
      // Set default active role
      if (userData.roles && userData.roles.length > 0) {
        setActiveRole(userData.roles[0]); // Set first role as default
      } else if (userData.role) {
        setActiveRole(userData.role);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on explicit 401 (invalid/expired token)
      // For network errors or 500s, keep user logged in
      if (error.response?.status === 401) {
        logout();
      }
      // For other errors, clear loading but don't logout
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh token every 6 hours to prevent session expiry
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token && user) {
        try {
          const response = await axios.post(`${API}/auth/refresh-token`);
          const newToken = response.data.access_token;
          localStorage.setItem('token', newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        } catch (err) {
          // Silent fail - token refresh is best-effort
          console.warn('Token refresh failed:', err?.message);
        }
      }
    }, 6 * 60 * 60 * 1000); // 6 hours
    return () => clearInterval(refreshInterval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Set active role based on user's roles
      if (userData.roles && userData.roles.length > 0) {
        setActiveRole(userData.roles[0]);
      } else if (userData.role) {
        setActiveRole(userData.role);
      }
      
      toast.success('Login effettuato con successo!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il login';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      toast.success('Registrazione completata! Attendi l\'approvazione dell\'admin.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setActiveRole(null);
    toast.success('Logout effettuato');
  };

  const switchRole = (role) => {
    // Check if user has this role
    if (user?.roles?.includes(role) || user?.role === role) {
      setActiveRole(role);
      toast.success(`Passato al ruolo: ${role.toUpperCase()}`);
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, activeRole, login, register, logout, switchRole, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App Component
function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
        <BrowserRouter>
          <Navbar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/eventi-passati" element={<PastEventsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <Dashboard />
                  </ErrorBoundary>
                </ProtectedRoute>
              } 
            />
          </Routes>
          <ScrollToTop />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
